import { useCallback } from "react";

import { $getRoot } from "lexical";

import { reportClientError } from "../../lib/client-error.js";
import { logger } from "../../lib/logger.js";
import type { SerializedEditorStateJson } from "../../lib/storage/index.js";
import { useStorage } from "../../lib/storage/storage-context.js";
import { useDraftsStore } from "../../stores/drafts-store.js";
import { useEditorInstance } from "../../stores/editor-instance.js";
import { useMediaStore } from "../../stores/media-store.js";

/**
 * Empty Lexical EditorState payload, matching what the editor produces on
 * a fresh mount (root with one empty paragraph). Mirrored from
 * `packages/client/src/services/draft-service.ts:createEmptyEditorState`.
 * Inlined here to avoid depending on the Lexical editor instance being
 * mounted before we can persist a new draft.
 */
const EMPTY_EDITOR_STATE: SerializedEditorStateJson = {
  root: {
    children: [
      {
        children: [],
        direction: null,
        format: "",
        indent: 0,
        type: "paragraph",
        version: 1,
        textFormat: 0,
        textStyle: "",
      },
    ],
    direction: null,
    format: "",
    indent: 0,
    type: "root",
    version: 1,
  },
} as unknown as SerializedEditorStateJson;

/**
 * Create-a-new-draft action shared by the editor header button and the
 * drafts screen's "New" button. Mirrors the desktop client's
 * `createDraft`: an empty record is written to storage immediately so the
 * new draft is visible in the list, then the editor is cleared and the
 * id is adopted as the active buffer for subsequent autosaves.
 *
 * In CloudStorage-unavailable contexts (older Telegram clients) we still
 * mint an id and clear the editor so local autosave continues working,
 * we just skip the remote write.
 */
export function useNewDraft(): () => Promise<void> {
  const { drafts, cloudAvailable } = useStorage();
  const setActiveDraftId = useDraftsStore((s) => s.setActiveDraftId);
  const setScreen = useDraftsStore((s) => s.setScreen);
  const upsertMeta = useDraftsStore((s) => s.upsertMeta);

  return useCallback(async (): Promise<void> => {
    const id = crypto.randomUUID();

    // Clear the live editor in-place. We do this synchronously inside the
    // Lexical update — `clear()` removes all root children, leaving a
    // single empty paragraph behind (Lexical's normal "empty" shape).
    const editor = useEditorInstance.getState().editor;
    if (editor) {
      editor.update(() => {
        $getRoot().clear();
      });
    }

    useMediaStore.getState().clear();

    if (cloudAvailable) {
      try {
        const meta = await drafts.save(id, EMPTY_EDITOR_STATE, "Untitled");
        upsertMeta(meta);
      } catch (err) {
        logger.error("createDraft: drafts.save failed", err);
        // Failures are surfaced to cluster logs so storage regressions
        // (e.g. Telegram tightening CloudStorage key validation) are
        // noticed quickly rather than silently breaking drafts.
        reportClientError({
          source: "manual",
          message: "miniapp_new_draft_save_failed",
          stack:
            err instanceof Error
              ? `${err.message}\n${err.stack ?? ""}`
              : String(err),
        });
      }
    }

    setActiveDraftId(id);
    setScreen("editor");
  }, [drafts, cloudAvailable, setActiveDraftId, setScreen, upsertMeta]);
}
